import { Trans, useTranslation } from "react-i18next";
import { Anchor, Body1, Box, Heading2, Image, Stack } from "@myphx-engineering/phx-design-system";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/components/MaintenancePage.tsx
/**
* MaintenancePage component displays a full-page maintenance screen
* with built-in translations for English and Spanish
*
* Note: The app must load the 'maintenance' namespace for translations to work.
*
* @example
* ```tsx
* const maintenanceStatus = useMaintenanceStatus(maintenanceWindow);
*
* <MaintenancePage
*   maintenanceStatus={maintenanceStatus}
*   logo={{
*     src: '/logo.png',
*     alt: 'Company Logo',
*     width: 128,
*     height: 48,
*   }}
*   illustration={<MaintenanceIllustration />}
*   onContactSupport={() => openChat()}
* />
* ```
*/
const MaintenancePage = ({ maintenanceStatus, logo, illustration, onContactSupport }) => {
	const { t } = useTranslation("maintenance");
	const { timeFrame } = maintenanceStatus;
	const { start, end } = timeFrame;
	const subtitleMessage = start && end ? t("page.subtitleWithWindow", {
		start,
		end
	}) : t("page.subtitleWithoutWindow");
	return /* @__PURE__ */ jsxs(Box, {
		p: "md",
		children: [logo && /* @__PURE__ */ jsx(Image, {
			src: logo.src,
			alt: logo.alt,
			width: logo.width || 128,
			height: logo.height || 48,
			fit: "contain"
		}), /* @__PURE__ */ jsxs(Stack, {
			gap: "md",
			justify: "center",
			align: "center",
			p: "md",
			children: [illustration, /* @__PURE__ */ jsxs(Stack, {
				gap: "sm",
				justify: "center",
				align: "center",
				ta: "center",
				children: [
					/* @__PURE__ */ jsx(Heading2, { children: t("page.title") }),
					/* @__PURE__ */ jsx(Body1, { children: subtitleMessage }),
					/* @__PURE__ */ jsx(Body1, { children: /* @__PURE__ */ jsx(Trans, {
						i18nKey: "page.supportText",
						t,
						components: { 1: onContactSupport ? /* @__PURE__ */ jsx(Anchor, { onClick: onContactSupport }) : /* @__PURE__ */ jsx("span", {}) }
					}) })
				]
			})]
		})]
	});
};

//#endregion
export { MaintenancePage };